/*
 * SammyGift reviews widget. Fills <div id="sg-reviews" data-product-id data-endpoint> from the App Proxy:
 *   GET {endpoint}?product_id=<id>  ->  { reviews: [{ rating, author_name, content, created_at, country_code, photos: [url] }],
 *                                        summary: { count, average, histogram } }
 * histogram may be an object {"5": n, ...} or an array [n1, n2, n3, n4, n5] (index 0 = 1 star).
 * Elements with class "sg-reviews" and data-endpoint but no product id (homepage, /pages/all-reviews-page) get the
 * store-wide list (latest reviews of all products); each review then also has product_handle (and product_title when the
 * API provides it) and is rendered with a link to the product.
 * Pages are fetched from the API with per=data-page-size; "Show more reviews" fetches the next page.
 * Fails silently (renders nothing) on any network/HTTP/JSON error, e.g. while the endpoint does not exist yet.
 */
(() => {
  const esc = (s) =>
    String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);

  const stars = (rating) => {
    const pct = Math.max(0, Math.min(100, (Number(rating) || 0) * 20));
    return `<span class="sg-review__stars" role="img" aria-label="${esc((Number(rating) || 0).toFixed(1))} out of 5 stars"><span style="width:${pct}%"></span></span>`;
  };

  const flag = (cc) =>
    /^[A-Za-z]{2}$/.test(cc || '')
      ? String.fromCodePoint(...cc.toUpperCase().split('').map((c) => 0x1f1a5 + c.charCodeAt(0)))
      : '';

  const fmtDate = (d) => {
    const t = Date.parse(d);
    return Number.isNaN(t) ? '' : new Date(t).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
  };

  // Absolute https URLs or same-origin App Proxy paths (review photos are served as /apps/sg/reviews/photo/<file>).
  const safeUrl = (u) => (/^(https:\/\/|\/apps\/sg\/)/i.test(String(u || '')) ? String(u) : '');

  const HANDLE_RE = /^[a-z0-9][a-z0-9-]*$/i;
  const titleFromHandle = (h) => h.split('-').filter(Boolean).map((w) => w[0].toUpperCase() + w.slice(1)).join(' ');
  function productLink(r) {
    const handle = HANDLE_RE.test(r.product_handle || '') ? r.product_handle : '';
    const title = r.product_title || (handle ? titleFromHandle(handle) : '');
    if (!title) return '';
    if (!handle) return `<div class="sg-review__product">${esc(title)}</div>`;
    const root = (window.Shopify?.routes?.root || '/').replace(/\/?$/, '/');
    return `<div class="sg-review__product"><a class="link" href="${esc(`${root}products/${handle}`)}">${esc(title)}</a></div>`;
  }

  function histogramRows(summary) {
    const h = summary?.histogram;
    if (!h) return '';
    const get = (n) => Number(Array.isArray(h) ? h[n - 1] : h[n] ?? h[String(n)]) || 0;
    const total = [1, 2, 3, 4, 5].reduce((a, n) => a + get(n), 0) || 1;
    return `<div class="sg-reviews__hist">${[5, 4, 3, 2, 1]
      .map((n) => `<span>${n}&#9733;</span><span class="sg-reviews__hist-bar"><span style="width:${((get(n) / total) * 100).toFixed(1)}%"></span></span><span>${get(n)}</span>`)
      .join('')}</div>`;
  }

  function reviewItem(r) {
    const photos = (Array.isArray(r.photos) ? r.photos : [])
      .map(safeUrl)
      .filter(Boolean)
      .map((u) => `<a href="${esc(u)}" target="_blank" rel="noopener"><img src="${esc(u)}" alt="Review photo" loading="lazy" width="80" height="80"></a>`)
      .join('');
    return `<li class="sg-review">
      <div class="sg-review__meta">${stars(r.rating)}<span class="sg-review__author">${esc(r.author_name || 'Anonymous')}</span>
        ${flag(r.country_code) ? `<span aria-label="${esc(r.country_code)}">${flag(r.country_code)}</span>` : ''}
        <span>${esc(fmtDate(r.created_at))}</span></div>
      ${productLink(r)}
      ${r.content ? `<p class="sg-review__content">${esc(r.content)}</p>` : ''}
      ${photos ? `<div class="sg-review__photos">${photos}</div>` : ''}
    </li>`;
  }

  async function fetchPage(endpoint, id, page, per) {
    const sep = endpoint.includes('?') ? '&' : '?';
    const params = new URLSearchParams({ page: String(page), per: String(per) });
    // Without a product id the endpoint returns store-wide reviews.
    if (id) params.set('product_id', id);
    const res = await fetch(`${endpoint}${sep}${params}`, { headers: { Accept: 'application/json' } });
    if (!res.ok) throw new Error(String(res.status));
    return res.json();
  }

  async function load(el) {
    if (el.dataset.sgLoaded) return;
    el.dataset.sgLoaded = '1';
    const id = el.dataset.productId;
    const endpoint = el.dataset.endpoint || '/apps/sg/reviews';
    const pageSize = Math.min(50, parseInt(el.dataset.pageSize || '10', 10) || 10);
    let data;
    try {
      data = await fetchPage(endpoint, id, 1, pageSize);
    } catch (e) {
      return; // endpoint missing / not JSON: stay silent
    }
    const reviews = Array.isArray(data?.reviews) ? data.reviews : [];
    const summary = data?.summary || {};
    if (!reviews.length && !summary.count) return;

    const avg = Number(summary.average) || 0;
    el.innerHTML = `
      ${summary.count ? `<div class="sg-reviews__summary">
        <div><div class="sg-reviews__average">${avg.toFixed(1)}</div>${stars(avg)}
        <div class="caption">${esc(summary.count)} ${Number(summary.count) === 1 ? 'review' : 'reviews'}</div></div>
        ${histogramRows(summary)}</div>` : ''}
      <ul class="sg-reviews__list" role="list"></ul>`;
    const list = el.querySelector('.sg-reviews__list');
    list.insertAdjacentHTML('beforeend', reviews.map(reviewItem).join(''));

    let page = 1;
    const pages = Number(data.pages) || 1;
    if (page >= pages) return;
    const more = document.createElement('button');
    more.type = 'button';
    more.className = 'button button--secondary sg-reviews__more';
    more.textContent = 'Show more reviews';
    more.addEventListener('click', async () => {
      more.disabled = true;
      try {
        const next = await fetchPage(endpoint, id, page + 1, pageSize);
        page += 1;
        const items = Array.isArray(next?.reviews) ? next.reviews : [];
        list.insertAdjacentHTML('beforeend', items.map(reviewItem).join(''));
        if (page >= (Number(next.pages) || 1) || !items.length) more.remove();
      } catch (e) {
        /* keep the button for a retry */
      }
      more.disabled = false;
    });
    el.append(more);
  }

  const init = () => document.querySelectorAll('#sg-reviews, .sg-reviews[data-endpoint]').forEach(load);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
